import React, {useRef, useState} from 'react';
import {ScheduledTasksApi} from "api/ScheduledTasksApi";
import {downloadStringAsFile} from "utils/file-utils";
import {ScheduledTaskDTO} from "api/model/airflow-task/ScheduledTaskDTO";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {Calendar} from 'primereact/calendar';
import {DEFAULT_DATE_FORMAT} from "utils/constants";
import {DateTimeFormatter, Instant, ZoneId} from '@js-joda/core';
import {Menu} from 'primereact/menu';
import {SplitButton} from 'primereact/splitbutton';
import { T } from 'services/lang/LanguageService';


interface TaskDataDownloadDialogProps {
    taskCfg: ScheduledTaskDTO;
}

function TaskDataDownloadButton(props: TaskDataDownloadDialogProps) {
    const menu = useRef<Menu>(null);

    const [downloading, setDataDownloading] = useState(false);
    const [date, setDate] = useState(new Date());
    const [invalid, setInvalid] = useState(false);
    const [showDialog, setShowDialog] = useState(false);


    const handleDownloadData = (refDate?: Date) => {
        setDataDownloading(true);

        const usedDate = refDate ?? date;
        const instNow = Instant.ofEpochMilli(usedDate.getTime());

        ScheduledTasksApi.getAirflowTaskJobData(
            props.taskCfg?.parentId!!,
            props.taskCfg?.id!!,
            instNow.toString()
        ).then((r) => {
            if (r.data) {
                // replace colons in date string to make it safe for file name and remove milliseconds
                const formatter = DateTimeFormatter.ofPattern("yyyy_MM_dd-HHmmss");
                const fileSafeDate = formatter.format(instNow.atZone(ZoneId.UTC));
                downloadStringAsFile(
                    JSON.stringify(r.data, null, 2),
                    `${props.taskCfg.parentId}:${props.taskCfg?.id}-${fileSafeDate}-taskdata.json`,
                    () => setDataDownloading(false)
                );
                setShowDialog(false);
            } else {
                easasAlert({ summary: T("alert.error-downloading-data-or-data-empty"), severity: "error" });
            }

            setDataDownloading(false);
        });
    };

    const handleDateChange = (currentDate: Date) => {
        if(isNaN(currentDate.valueOf())) {
            setInvalid(true)
        } else {
            setDate(currentDate);
            setInvalid(false)
        }
    }

    const openDialog = () => {
        setShowDialog(true);
        setDate(new Date());
    }

    const menuItems = [
        {
            label: T("action.download-now"),
            icon: "pi pi-download",
            command: () => handleDownloadData(new Date()),
        },
        {
            label: T("action.download-at"),
            icon: "pi pi-calendar",
            command: () => openDialog(),
        }
    ];


    return <>
        <Menu model={menuItems} popup ref={menu} id="popup-menu-data-dl" />
        <SplitButton
            label={`${T("word.data")} JSON...`}
            icon="pi pi-download"
            className="p-button-pain"
            model={menuItems}
            onClick={() => handleDownloadData(new Date())}
        />

        <Dialog header={T("label.download-task-data-json")} visible={showDialog} onHide={() => setShowDialog(false)} style={{width: '300px'}}>
            <div className="p-fluid grid formgrid">
                <div className="p-field p-col-12">
                    <span>
                        { !invalid && <span>{T("word.reference-date")}</span>}
                        { invalid && <>
                            {T("error.invalid-date")}
                            <i className="pi pi-exclamation-triangle ml-2" style={{color: "crimson"}} />
                        </>}
                    </span>
                    <Calendar showTime value={date}
                              dateFormat={DEFAULT_DATE_FORMAT}
                              hourFormat="24"
                              keepInvalid
                              onChange={(e) => handleDateChange(e.value as Date)} />
                </div>

                <div className="p-field p-col-12">
                    <Button label={T("action.download")} icon="pi pi-download" className="p-button-success"
                            onClick={() => handleDownloadData()} loading={downloading} disabled={invalid}/>
                </div>
            </div>
        </Dialog>
    </>
}

export default TaskDataDownloadButton;